import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "@/lib/api";
import {
  connectQz,
  disconnectQz,
  isConnected as qzIsConnected,
  listPrinters,
  printReceipt as qzPrintReceipt,
  getSavedPrinter,
  saveSavedPrinter,
  buildLogoBase64,
} from "@/lib/printer";
import { LogOut, RefreshCcw, ChefHat, Package, Clock, Check, Printer, PlugZap, Pencil, Plus, Upload, Trash2, KeyRound, Bell, BellOff } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { playChime, unlockChime } from "@/lib/chime";
import AnalyticsPanel from "./admin/AnalyticsPanel";
import FeedbackPanel from "./admin/FeedbackPanel";
import SponsorsPanel from "./admin/SponsorsPanel";

const COLUMNS = [
  { key: "pending", label: "Pending", icon: Clock, color: "#ca8a04" },
  { key: "preparing", label: "Preparing", icon: ChefHat, color: "#1d4ed8" },
  { key: "ready", label: "Ready", icon: Package, color: "#16a34a" },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const token = localStorage.getItem("gh_admin_token");
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [menu, setMenu] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, preparing: 0, ready: 0 });
  const [settings, setSettings] = useState(null);
  const [autoPrint, setAutoPrint] = useState(
    () => localStorage.getItem("gh_auto_print") === "true"
  );
  const [chimeEnabled, setChimeEnabled] = useState(
    () => localStorage.getItem("gh_chime_enabled") !== "false" // default ON
  );
  const [chimeReady, setChimeReady] = useState(false);
  const [qzConnected, setQzConnected] = useState(false);
  const [printers, setPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState(getSavedPrinter());
  const [showPrinterSetup, setShowPrinterSetup] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showOwner, setShowOwner] = useState(false);
  const seenOrders = useRef(new Set());
  const firstLoad = useRef(true);
  // Timestamp of the last overdue-reminder chime we played. We only ring
  // every 3 minutes even if there are multiple overdue orders — enough to
  // nag staff without becoming an annoyance.
  const lastOverdueChimeAt = useRef(0);

  const client = adminApi(token);

  // Attempt to reconnect to QZ Tray on mount if it was previously used
  useEffect(() => {
    if (!getSavedPrinter()) return;
    connectQz()
      .then(() => setQzConnected(true))
      .catch(() => setQzConnected(false));
    return () => {
      // Do not auto-disconnect on unmount; keep QZ session alive across nav
    };
  }, []);

  // Browsers block audio until the user interacts with the page. Unlock the
  // chime on the first click/keypress/touch so subsequent playChime() calls
  // during background polling actually make sound.
  useEffect(() => {
    if (chimeReady) return;
    const unlock = () => {
      if (unlockChime()) {
        setChimeReady(true);
        window.removeEventListener("pointerdown", unlock);
        window.removeEventListener("keydown", unlock);
      }
    };
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => {
      window.removeEventListener("pointerdown", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, [chimeReady]);

  const handleConnectQz = async () => {
    try {
      await connectQz();
      const found = await listPrinters();
      setPrinters(found);
      setQzConnected(true);
      toast.success(`QZ Tray connected · ${found.length} printers found`);
    } catch (e) {
      setQzConnected(false);
      toast.error(
        "QZ Tray not detected. Install from qz.io/download and launch it."
      );
    }
  };

  const handleDisconnectQz = async () => {
    await disconnectQz();
    setQzConnected(false);
    toast.success("QZ Tray disconnected");
  };

  const handleSelectPrinter = (name) => {
    setSelectedPrinter(name);
    saveSavedPrinter(name);
    toast.success(`Printer set: ${name}`);
  };

  const printOrder = async (order, opts = {}) => {
    const { isAutoPrint = false } = opts;
    const printer = getSavedPrinter();

    // Preferred path: ESC/POS via QZ Tray → USB → Rongta.
    if (printer) {
      try {
        // Reconnect if the websocket dropped (browser tab sleep, network
        // hiccup, or QZ Tray restart). Without this the auto-print check
        // silently misses the printer even though it's still available.
        if (!qzIsConnected()) {
          await connectQz();
        }
        await qzPrintReceipt(order, printer);
        return;
      } catch (e) {
        const msg = e?.message || "printer offline";
        if (isAutoPrint) {
          // Do NOT open a popup for auto-print — background window.open()
          // calls (not from a user click) are blocked by every modern
          // browser, so we'd just fail silently. Surface a clear error
          // instead so staff can fix the QZ connection.
          toast.error(`Auto-print failed for ${order.order_number}: ${msg}`, {
            duration: 6000,
          });
          return;
        }
        toast.error(`Print failed: ${msg}`);
        // Fall through to browser fallback for user-initiated prints only.
      }
    } else if (isAutoPrint) {
      // No printer configured — auto-print has nowhere to send the job.
      toast.error(
        `Auto-print skipped: open Printer Setup and pick a printer first.`,
        { duration: 8000 }
      );
      return;
    }

    // Fallback: browser popup (only reliable when user-initiated).
    browserPrintFallback(order);
  };

  const browserPrintFallback = (order) => {
    const esc = (s) =>
      String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
    const dateStr = new Date(order.created_at).toLocaleString();
    const rows = order.items
      .map((it) => {
        const extras = [it.variant_choice, it.side_choice, it.drink_choice]
          .filter(Boolean)
          .map(esc)
          .join(" · ");
        const addons = Array.isArray(it.addons) && it.addons.length
          ? it.addons
              .map((a) => `+ ${esc(a.label)}${a.qty > 1 ? " ×" + esc(a.qty) : ""}`)
              .join(" · ")
          : "";
        const extrasLine = extras ? "<br/><small>" + extras + "</small>" : "";
        const addonsLine = addons ? "<br/><small>" + addons + "</small>" : "";
        return `<tr><td>${esc(it.quantity)}×</td><td>${esc(it.name)}${extrasLine}${addonsLine}</td></tr>`;
      })
      .join("");
    const html = `<!doctype html><html><head><title>Order ${esc(order.order_number)}</title>
      <style>
        @media print { @page { margin: 6mm; size: 80mm auto; } }
        body { font-family: monospace; font-size: 13px; margin: 8px; }
        h1 { font-size: 26px; text-align: center; margin: 4px 0; letter-spacing: 3px; }
        h2 { font-size: 15px; text-align: center; margin: 0 0 8px; font-weight: normal; }
        table { width: 100%; border-collapse: collapse; }
        td { padding: 4px 2px; vertical-align: top; border-bottom: 1px dotted #999; }
        td:first-child { width: 32px; }
        .row { display: flex; justify-content: space-between; margin-top: 3px; }
        .total { font-size: 16px; font-weight: bold; border-top: 2px solid #000; padding-top: 6px; margin-top: 6px; }
        .divider { border-top: 1px dashed #000; margin: 8px 0; }
      </style></head><body>
      <h1>${esc(order.order_number)}</h1>
      <h2>GAITHER COWBOYS · CONCESSIONS</h2>
      <div class="divider"></div>
      <div><strong>${esc(order.customer_name)}</strong> · ${esc(order.customer_phone)}</div>
      <div style="font-size:11px;color:#555;">${esc(dateStr)}</div>
      <div class="divider"></div>
      <table>${rows}</table>
      <div class="row"><span>Subtotal</span><span>$${order.subtotal.toFixed(2)}</span></div>
      <div class="row"><span>Online fee</span><span>$${order.surcharge.toFixed(2)}</span></div>
      ${
        Number(order.tip || 0) > 0
          ? `<div class="row"><span>Tip</span><span>$${Number(order.tip).toFixed(2)}</span></div>`
          : ""
      }
      <div class="row total"><span>TOTAL</span><span>$${order.total.toFixed(2)}</span></div>
      <div class="divider"></div>
      <div style="text-align:center;font-size:11px;">GO COWBOYS! · Pick up at the window</div>
      <script>window.onload=function(){setTimeout(function(){window.print();},80);};<\/script>
    </body></html>`;

    // Render via a Blob URL rather than document.write — same output, no
    // synchronous parser-write sink. Every dynamic value above is already
    // passed through esc() so the HTML is safe by construction.
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank", "width=340,height=600");
    if (!win) {
      URL.revokeObjectURL(url);
      toast.error("Enable pop-ups or set up QZ Tray for reliable printing");
      return;
    }
    // Free the blob URL after the popup has had a chance to load it.
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const load = useCallback(async () => {
    try {
      const [o, m, s, cfg] = await Promise.all([
        client.get("/admin/orders"),
        client.get("/admin/menu"),
        client.get("/admin/stats"),
        client.get("/settings"),
      ]);
      const nextOrders = o.data;

      // Auto-print any new orders we've never seen before (skip on very first mount)
      if (!firstLoad.current) {
        let sawNew = false;
        for (const ord of nextOrders) {
          if (!seenOrders.current.has(ord.id)) {
            sawNew = true;
            // Skip auto-print for scheduled/delayed pickups — staff hasn't
            // committed to cooking them yet. The chime still rings so the
            // order is noticed, and staff can hit "Start Preparing" (which
            // will print) when the requested time approaches.
            if (autoPrint && !ord.scheduled_for) {
              printOrder(ord, { isAutoPrint: true });
            }
          }
        }
        // Ring the bell once per polling tick if any new order arrived —
        // avoids stacking chimes when a burst of orders lands together.
        if (sawNew && chimeEnabled) {
          playChime();
        }
      }
      seenOrders.current = new Set(nextOrders.map((o) => o.id));
      firstLoad.current = false;

      // Overdue-repeat chime: any order that's been Ready for ≥15 minutes
      // triggers a gentle reminder every 3 minutes until it's picked up.
      // Uses the same chime the new-order bell uses, at a slightly softer
      // volume so staff can tell the two apart.
      if (chimeEnabled) {
        const now = Date.now();
        const overdue = nextOrders.some((ord) => {
          if (ord.status !== "ready") return false;
          const t = new Date(ord.updated_at || ord.created_at).getTime();
          return now - t >= 15 * 60 * 1000;
        });
        if (overdue && now - lastOverdueChimeAt.current >= 3 * 60 * 1000) {
          playChime(0.25);
          lastOverdueChimeAt.current = now;
        }
      }

      setOrders(nextOrders);
      setMenu(m.data);
      setStats(s.data);
      setSettings(cfg.data);
    } catch (e) {
      if (e.response?.status === 401) {
        localStorage.removeItem("gh_admin_token");
        navigate("/admin");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, autoPrint, chimeEnabled]);

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    load();
    const i = setInterval(load, 4000);
    return () => clearInterval(i);
  }, [token, load, navigate]);

  const updateOrderStatus = async (orderId, status) => {
    try {
      await client.patch(`/admin/orders/${orderId}`, { status });
      toast.success(`Marked ${status}`, { duration: 1500 });
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const toggleAvailable = async (item) => {
    try {
      await client.patch(`/admin/menu/${item.id}`, { available: !item.available });
      load();
    } catch {
      toast.error("Toggle failed");
    }
  };

  const saveMenuItem = async (payload) => {
    try {
      if (editingItem?.__new) {
        await client.post("/admin/menu", payload);
        toast.success(`${payload.name} added`);
      } else {
        await client.patch(`/admin/menu/${editingItem.id}`, payload);
        toast.success(`${payload.name || editingItem.name} updated`);
      }
      setEditingItem(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const deleteMenuItem = async (item) => {
    if (!window.confirm(`Permanently delete "${item.name}"? Existing orders are not affected.`)) return;
    try {
      await client.delete(`/admin/menu/${item.id}`);
      toast.success(`${item.name} deleted`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const updateSettings = async (patch) => {
    if (!settings) return;
    // Optimistic — flip the toggle immediately so UI stays snappy, roll back on error.
    const prev = settings;
    setSettings((s) => ({ ...s, ...patch }));
    try {
      const { data } = await client.patch("/admin/settings", patch);
      setSettings((s) => ({ ...s, ...data }));
      if ("orders_open" in patch) {
        toast.success(patch.orders_open ? "Orders are now OPEN" : "Orders paused");
      } else if ("active_sport" in patch) {
        toast.success(`Sport set to ${patch.active_sport}`);
      }
    } catch (e) {
      setSettings(prev);
      toast.error(e.response?.data?.detail || "Settings update failed");
    }
  };

  const logout = () => {
    localStorage.removeItem("gh_admin_token");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <header className="border-b-2 border-slate-900 bg-white">
        <div className="flex items-center justify-between px-5 py-4 sm:px-8">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center bg-[#1d4ed8] text-white border border-slate-900">
              <span className="font-heading text-xl">G</span>
            </div>
            <div className="leading-tight">
              <div className="font-heading text-lg tracking-wider">
                STAFF DASHBOARD
              </div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                Cowboys Concession Ops
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="printer-setup-btn"
              onClick={() => setShowPrinterSetup(true)}
              className={`flex items-center gap-2 border px-3 py-2 text-[10px] uppercase tracking-[0.2em] ${
                qzConnected
                  ? "border-[#16a34a] text-[#16a34a]"
                  : "border-slate-300 text-slate-500 hover:border-[#1d4ed8] hover:text-[#1d4ed8]"
              }`}
              title="Printer setup"
            >
              <PlugZap className="h-3.5 w-3.5" />
              {qzConnected
                ? selectedPrinter
                  ? selectedPrinter.length > 12
                    ? selectedPrinter.slice(0, 12) + "…"
                    : selectedPrinter
                  : "QZ · Pick Printer"
                : "Printer Setup"}
            </button>
            <label className="hidden sm:flex items-center gap-2 border border-slate-300 px-3 py-2 cursor-pointer">
              <Printer className="h-4 w-4 text-slate-500" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-600">
                Auto-Print
              </span>
              <Switch
                data-testid="auto-print-toggle"
                checked={autoPrint}
                onCheckedChange={(v) => {
                  setAutoPrint(v);
                  localStorage.setItem("gh_auto_print", String(v));
                  if (v) toast.success("New orders will print automatically");
                }}
              />
            </label>
            <button
              data-testid="chime-toggle-btn"
              onClick={() => {
                const next = !chimeEnabled;
                setChimeEnabled(next);
                localStorage.setItem("gh_chime_enabled", String(next));
                if (next) {
                  // Unlock + preview the sound so staff know it works.
                  if (unlockChime()) setChimeReady(true);
                  playChime();
                  toast.success("Chime on — you'll hear a bell on new orders");
                } else {
                  toast.success("Chime off");
                }
              }}
              className={`hidden sm:flex h-9 w-9 items-center justify-center border ${
                chimeEnabled
                  ? "border-[#1d4ed8] text-[#1d4ed8]"
                  : "border-slate-300 text-slate-400"
              } hover:border-[#1d4ed8] hover:text-[#1d4ed8]`}
              title={
                chimeEnabled
                  ? chimeReady
                    ? "Chime on — click to mute"
                    : "Chime on — click anywhere to activate audio"
                  : "Chime muted — click to enable"
              }
              aria-label={chimeEnabled ? "Mute new-order chime" : "Enable new-order chime"}
            >
              {chimeEnabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
            </button>
            <button
              data-testid="print-last-btn"
              onClick={() => orders[0] && printOrder(orders[0])}
              disabled={orders.length === 0}
              className="flex h-9 w-9 items-center justify-center border border-slate-300 text-slate-500 hover:border-[#1d4ed8] hover:text-[#1d4ed8] disabled:opacity-40"
              title="Print most recent order"
            >
              <Printer className="h-4 w-4" />
            </button>
            <button
              data-testid="refresh-btn"
              onClick={load}
              className="flex h-9 w-9 items-center justify-center border border-slate-300 text-slate-500 hover:border-[#1d4ed8] hover:text-[#1d4ed8]"
              title="Refresh"
            >
              <RefreshCcw className="h-4 w-4" />
            </button>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="flex items-center gap-2 border border-slate-300 px-3 py-2 text-xs uppercase tracking-[0.2em] text-slate-500 hover:border-[#1d4ed8] hover:text-[#1d4ed8]"
            >
              <LogOut className="h-3.5 w-3.5" />
              Sign Out
            </button>
          </div>
        </div>

        {/* Intake control bar — hidden until first settings fetch resolves,
            to avoid flashing 'PAUSED' red on every admin page load. */}
        {settings && (
        <div
          data-testid="intake-controls"
          className={`flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-5 py-3 sm:px-8 ${
            settings.orders_open ? "bg-emerald-50" : "bg-red-50"
          }`}
        >
          <div className="flex items-center gap-3">
            <span
              className={`inline-block h-2.5 w-2.5 rounded-full ${
                settings.orders_open ? "bg-[#16a34a] animate-pulse" : "bg-red-500"
              }`}
            />
            <div>
              <div className="font-heading text-sm tracking-[0.2em]">
                {settings.orders_open ? "ACCEPTING ORDERS" : "ORDERS PAUSED"}
              </div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Fee ${Number(settings.surcharge || 0).toFixed(2)} · Customers {settings.orders_open ? "can" : "cannot"} check out
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="owner-settings-btn"
              onClick={() => setShowOwner(true)}
              className="flex items-center gap-1.5 border border-slate-300 bg-white px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:border-[#1d4ed8] hover:text-[#1d4ed8]"
              title="Owner settings"
            >
              <KeyRound className="h-3.5 w-3.5" />
              Owner
            </button>
            <label className="flex items-center gap-2 border border-slate-300 bg-white px-3 py-1.5">
              <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500">
                Sport
              </span>
              <select
                data-testid="active-sport-select"
                value={settings.active_sport}
                onChange={(e) => updateSettings({ active_sport: e.target.value })}
                className="bg-white text-sm font-medium text-slate-900 focus:outline-none"
              >
                {(settings.sports?.length ? settings.sports : [settings.active_sport]).map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </label>
            <label className="flex items-center gap-2 border-2 border-slate-900 bg-white px-3 py-1.5 cursor-pointer">
              <span className="font-heading text-xs tracking-[0.2em] text-slate-900">
                {settings.orders_open ? "ON" : "OFF"}
              </span>
              <Switch
                data-testid="orders-open-toggle"
                checked={settings.orders_open}
                onCheckedChange={(v) => updateSettings({ orders_open: v })}
              />
            </label>
          </div>
        </div>
        )}

        <div className="grid grid-cols-2 gap-px border-t border-slate-200 bg-slate-200 sm:grid-cols-6">
          <Stat label="Total" value={stats.total} />
          <Stat label="Pending" value={stats.pending} color="#ca8a04" />
          <Stat label="Preparing" value={stats.preparing} color="#1d4ed8" />
          <Stat label="Ready" value={stats.ready} color="#16a34a" />
          <Stat
            label="Profit Today"
            value={`$${Number(stats.profit_today || 0).toFixed(0)}`}
            color="#0f766e"
          />
          <Stat
            label="Promos Redeemed"
            value={
              <span
                data-testid="promos-redeemed-stat"
                title={`Deputy: ${stats.deputy_redemptions_today || 0} · Campaigns: ${stats.campaign_redemptions_today || 0}`}
              >
                {(stats.deputy_redemptions_today || 0) + (stats.campaign_redemptions_today || 0)}
              </span>
            }
            color="#7c3aed"
          />
        </div>

        <div className="flex overflow-x-auto border-t border-slate-200">
          <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} testid="tab-orders">
            Orders
          </TabBtn>
          <TabBtn active={tab === "menu"} onClick={() => setTab("menu")} testid="tab-menu">
            Menu
          </TabBtn>
          <TabBtn active={tab === "history"} onClick={() => setTab("history")} testid="tab-history">
            History
          </TabBtn>
          <TabBtn active={tab === "analytics"} onClick={() => setTab("analytics")} testid="tab-analytics">
            Analytics
          </TabBtn>
          <TabBtn active={tab === "promos"} onClick={() => setTab("promos")} testid="tab-promos">
            Promos
          </TabBtn>
          <TabBtn active={tab === "feedback"} onClick={() => setTab("feedback")} testid="tab-feedback">
            Feedback
          </TabBtn>
          <TabBtn active={tab === "sponsors"} onClick={() => setTab("sponsors")} testid="tab-sponsors">
            Sponsors
          </TabBtn>
        </div>
      </header>

      <main className="p-4 sm:p-6">
        {tab === "orders" && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {COLUMNS.map((col) => {
              const colOrders = orders.filter((o) => o.status === col.key);
              const Icon = col.icon;
              return (
                <div
                  key={col.key}
                  data-testid={`col-${col.key}`}
                  className="border-2 border-slate-900 bg-white"
                >
                  <div
                    className="flex items-center justify-between border-b border-slate-200 px-4 py-3"
                    style={{ borderTopColor: col.color, borderTopWidth: 4 }}
                  >
                    <div className="flex items-center gap-2">
                      <Icon
                        className="h-4 w-4"
                        style={{ color: col.color }}
                        strokeWidth={2.5}
                      />
                      <span className="font-heading text-base tracking-wider">
                        {col.label.toUpperCase()}
                      </span>
                    </div>
                    <span
                      className="font-heading text-xl tabular"
                      style={{ color: col.color }}
                    >
                      {colOrders.length}
                    </span>
                  </div>
                  <div className="space-y-px bg-slate-200">
                    {colOrders.length === 0 && (
                      <div className="bg-white px-4 py-8 text-center text-xs uppercase tracking-[0.2em] text-slate-400">
                        No orders
                      </div>
                    )}
                    {colOrders.map((o) => (
                      <OrderCard
                        key={o.id}
                        order={o}
                        onAdvance={updateOrderStatus}
                        onReprint={printOrder}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {tab === "menu" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                {menu.length} items · Prices &amp; costs shown are staff-only
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="menu-bulk-import-btn"
                  onClick={() => setShowBulkImport(true)}
                  className="flex items-center gap-2 border-2 border-slate-900 bg-white px-4 py-2 font-heading text-xs tracking-wider text-slate-900 hover:bg-slate-100"
                >
                  <Upload className="h-4 w-4" strokeWidth={2.5} />
                  BULK IMPORT
                </button>
                <button
                  data-testid="menu-new-item-btn"
                  onClick={() => setEditingItem({ __new: true })}
                  className="flex items-center gap-2 bg-[#1d4ed8] px-4 py-2 font-heading text-xs tracking-wider text-white hover:bg-[#1e40af]"
                >
                  <Plus className="h-4 w-4" strokeWidth={3} />
                  NEW ITEM
                </button>
              </div>
            </div>
            <div className="border-2 border-slate-900 bg-white overflow-x-auto">
              <div className="grid grid-cols-12 gap-2 border-b border-slate-200 bg-slate-100 px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 min-w-[720px]">
                <div className="col-span-4">Item</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-1 text-right">Price</div>
                <div className="col-span-1 text-right">Cost</div>
                <div className="col-span-1 text-right">Profit</div>
                <div className="col-span-3 text-right">Available · Edit</div>
              </div>
              {menu.map((it) => {
                const profit = Number(it.price || 0) - Number(it.cost || 0);
                return (
                  <div
                    key={it.id}
                    data-testid={`menu-row-${it.id}`}
                    className="grid grid-cols-12 items-center gap-2 border-b border-slate-200 bg-white px-4 py-3 last:border-b-0 min-w-[720px]"
                  >
                    <div className="col-span-4">
                      <div className="font-heading text-base tracking-wide">
                        {it.name}
                      </div>
                      <div className="text-xs text-slate-500 line-clamp-1">
                        {it.description || <span className="italic text-slate-400">No description</span>}
                      </div>
                      {Array.isArray(it.variants) && it.variants.length > 0 && (
                        <div className="mt-0.5 text-[10px] uppercase tracking-[0.2em] text-[#1d4ed8]">
                          Variants: {it.variants.join(" / ")}
                        </div>
                      )}
                    </div>
                    <div className="col-span-2 text-xs uppercase tracking-[0.2em] text-[#1d4ed8]">
                      {it.category}
                    </div>
                    <div className="col-span-1 text-right font-heading text-base text-[#1d4ed8] tabular">
                      ${Number(it.price || 0).toFixed(2)}
                    </div>
                    <div className="col-span-1 text-right font-heading text-sm text-slate-500 tabular">
                      ${Number(it.cost || 0).toFixed(2)}
                    </div>
                    <div className={`col-span-1 text-right font-heading text-sm tabular ${profit >= 0 ? "text-[#0f766e]" : "text-red-600"}`}>
                      ${profit.toFixed(2)}
                    </div>
                    <div className="col-span-3 flex items-center justify-end gap-3">
                      <span
                        className={`text-[10px] uppercase tracking-[0.2em] ${
                          it.available ? "text-[#16a34a]" : "text-slate-400"
                        }`}
                      >
                        {it.available ? "In Stock" : "Sold Out"}
                      </span>
                      <Switch
                        data-testid={`toggle-${it.id}`}
                        checked={it.available}
                        onCheckedChange={() => toggleAvailable(it)}
                      />
                      <button
                        data-testid={`edit-${it.id}-btn`}
                        onClick={() => setEditingItem(it)}
                        className="border border-slate-300 bg-white p-1.5 text-slate-500 hover:border-[#1d4ed8] hover:text-[#1d4ed8]"
                        aria-label="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid={`delete-${it.id}-btn`}
                        onClick={() => deleteMenuItem(it)}
                        className="border border-slate-300 bg-white p-1.5 text-slate-500 hover:border-red-600 hover:text-red-600"
                        aria-label="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {tab === "history" && (
          <HistoryPanel client={client} onReprint={printOrder} />
        )}
        {tab === "analytics" && (
          <AnalyticsPanel client={client} />
        )}
        {tab === "promos" && (
          <PromosPanel client={client} />
        )}
        {tab === "feedback" && (
          <FeedbackPanel client={client} />
        )}
        {tab === "sponsors" && (
          <SponsorsPanel client={client} />
        )}
      </main>
      {/* Printer setup modal */}
      {showPrinterSetup && (
        <div
          data-testid="printer-setup-modal"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
          onClick={() => setShowPrinterSetup(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg border-2 border-slate-900 bg-white p-6"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
                  Receipt Printer
                </div>
                <div className="font-heading text-2xl tracking-wider">
                  QZ TRAY · ESC/POS
                </div>
                <div className="mt-1 text-xs text-slate-600 max-w-md">
                  Sends raw ESC/POS commands to your Rongta (or any thermal
                  printer) via a local bridge — no Chrome print dialog.
                </div>
              </div>
              <button
                onClick={() => setShowPrinterSetup(false)}
                className="text-slate-400 hover:text-slate-900 font-heading text-2xl leading-none"
              >
                ×
              </button>
            </div>

            <ol className="mt-5 space-y-1.5 text-xs text-slate-700 border border-slate-200 bg-slate-50 p-3">
              <li>
                <span className="font-heading text-sm text-[#1d4ed8]">1.</span>{" "}
                Install{" "}
                <a
                  href="https://qz.io/download/"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#1d4ed8] underline"
                >
                  QZ Tray
                </a>{" "}
                on this computer (free, ~10 MB, Windows / Mac / Linux)
              </li>
              <li>
                <span className="font-heading text-sm text-[#1d4ed8]">2.</span>{" "}
                Launch QZ Tray — you'll see its icon in the system tray
              </li>
              <li>
                <span className="font-heading text-sm text-[#1d4ed8]">3.</span>{" "}
                Connect your Rongta via USB & install its Windows driver
              </li>
              <li>
                <span className="font-heading text-sm text-[#1d4ed8]">4.</span>{" "}
                Click <b>Connect</b> below — QZ Tray will prompt you once to allow this site
              </li>
              <li>
                <span className="font-heading text-sm text-[#1d4ed8]">5.</span>{" "}
                Select your Rongta printer from the list
              </li>
            </ol>

            <div className="mt-5 space-y-3">
              <div className="flex items-center gap-3">
                <div
                  className={`h-2.5 w-2.5 ${
                    qzConnected ? "bg-[#16a34a] animate-pulse" : "bg-slate-300"
                  }`}
                />
                <span className="text-xs uppercase tracking-[0.2em] text-slate-700">
                  {qzConnected ? "QZ Tray Connected" : "Not Connected"}
                </span>
                <div className="flex-1" />
                {qzConnected ? (
                  <button
                    data-testid="qz-disconnect-btn"
                    onClick={handleDisconnectQz}
                    className="border border-slate-300 px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:border-red-500 hover:text-red-500"
                  >
                    Disconnect
                  </button>
                ) : (
                  <button
                    data-testid="qz-connect-btn"
                    onClick={handleConnectQz}
                    className="bg-[#1d4ed8] px-4 py-2 font-heading text-sm tracking-wider text-white hover:bg-[#1e40af]"
                  >
                    CONNECT
                  </button>
                )}
              </div>

              {qzConnected && (
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-2">
                    Printer ({printers.length} found)
                  </div>
                  {printers.length === 0 && (
                    <div className="border border-slate-300 bg-slate-50 p-3 text-xs text-slate-600">
                      No printers detected. Make sure your Rongta is plugged in
                      and Windows sees it. Click Connect again to refresh.
                    </div>
                  )}
                  <div className="max-h-64 overflow-auto border border-slate-300 divide-y divide-slate-200">
                    {printers.map((name) => {
                      const active = selectedPrinter === name;
                      return (
                        <button
                          key={name}
                          data-testid={`printer-${name}`}
                          onClick={() => handleSelectPrinter(name)}
                          className={`w-full text-left px-3 py-2.5 flex items-center gap-3 ${
                            active
                              ? "bg-[#1d4ed8] text-white"
                              : "bg-white text-slate-900 hover:bg-slate-100"
                          }`}
                        >
                          <Printer className="h-4 w-4 flex-shrink-0" />
                          <span className="font-mono text-sm truncate">
                            {name}
                          </span>
                          {active && (
                            <span className="ml-auto text-[10px] uppercase tracking-[0.2em]">
                              Selected
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {qzConnected && selectedPrinter && orders[0] && (
                <button
                  data-testid="print-test-btn"
                  onClick={() => printOrder(orders[0])}
                  className="mt-2 w-full border-2 border-slate-900 bg-white py-2.5 font-heading text-sm tracking-wider text-slate-900 hover:bg-slate-100"
                >
                  PRINT TEST (MOST RECENT ORDER)
                </button>
              )}

              <button
                data-testid="run-calibration-btn"
                onClick={async () => {
                  try {
                    const { data } = await client.post("/admin/orders/calibration");
                    toast.success(`Calibration order ${data.order_number} created`);
                    load();
                    if (qzIsConnected() && getSavedPrinter()) {
                      await printOrder(data);
                    }
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Calibration failed");
                  }
                }}
                className="mt-2 w-full border-2 border-[#1d4ed8] bg-[#1d4ed8] py-2.5 font-heading text-sm tracking-wider text-white hover:bg-[#1e40af]"
              >
                RUN CALIBRATION ORDER + PRINT
              </button>

              <div className="mt-4 border border-slate-200 bg-slate-50 p-3">
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2">
                  Receipt Logo Preview
                </div>
                <img
                  data-testid="logo-preview-img"
                  alt="Gaither Cowboys receipt logo"
                  src={`data:image/png;base64,${buildLogoBase64()}`}
                  className="mx-auto block border border-slate-300 bg-white"
                  style={{ maxWidth: "100%", width: 320 }}
                />
                <div className="mt-1.5 text-center text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  Rasterized to ESC/POS bit-image at print time
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Menu item add/edit modal */}
      {editingItem && (
        <MenuEditModal
          item={editingItem.__new ? null : editingItem}
          onClose={() => setEditingItem(null)}
          onSave={saveMenuItem}
        />
      )}

      {/* Bulk import modal */}
      {showBulkImport && (
        <BulkImportModal
          client={client}
          onClose={() => setShowBulkImport(false)}
          onDone={() => {
            setShowBulkImport(false);
            load();
          }}
        />
      )}

      {/* Owner settings modal — password-gated, changes surcharge (only when paused) */}
      {showOwner && (
        <OwnerSettingsModal
          client={client}
          settings={settings}
          onClose={() => setShowOwner(false)}
          onSaved={(next) => {
            setSettings((s) => (s ? { ...s, ...next } : next));
            load();
          }}
        />
      )}
    </div>
  );
}

function Stat({ label, value, color = "#1d4ed8" }) {
  return (
    <div className="bg-white px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
        {label}
      </div>
      <div className="font-heading text-3xl tabular" style={{ color }}>
        {value}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-3 font-heading text-base tracking-wider transition-colors ${
        active
          ? "text-[#1d4ed8] border-b-2 border-[#1d4ed8]"
          : "text-slate-500 border-b-2 border-transparent hover:text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

function OrderCard({ order, onAdvance, onReprint }) {
  const next = {
    pending: { status: "preparing", label: "Start Preparing" },
    preparing: { status: "ready", label: "Mark Ready" },
    ready: { status: "completed", label: "Mark Picked Up" },
  }[order.status];

  const elapsedMin = Math.floor(
    (Date.now() - new Date(order.created_at).getTime()) / 60000
  );

  const isPending = order.status === "pending";
  const isReadyStatus = order.status === "ready";
  const readyElapsedMin = isReadyStatus
    ? Math.floor((Date.now() - new Date(order.updated_at || order.created_at).getTime()) / 60000)
    : 0;
  const isOverdue = isReadyStatus && readyElapsedMin >= 15;
  const isScheduled = !!order.scheduled_for;
  const itemCount = order.items.reduce((a, i) => a + (i.quantity || 0), 0);
  const feeCharged = Number(order.surcharge || 0);
  const wasFeeWaived = !!order.fee_waived;

  return (
    <div
      className={`bg-white p-3 ${isPending ? "border-l-4 border-l-[#ca8a04]" : ""} ${
        isOverdue ? "border-l-4 border-l-red-600" : ""
      } ${isScheduled && isPending ? "border-l-4 border-l-[#7c3aed]" : ""}`}
      data-testid={`order-card-${order.id}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-heading text-2xl text-[#1d4ed8] tabular">
              {order.order_number}
            </div>
            {isPending && !isScheduled && (
              <span
                data-testid="pending-new-badge"
                className="bg-[#ca8a04] px-1.5 py-0.5 font-heading text-[9px] tracking-[0.2em] text-white"
              >
                NEW
              </span>
            )}
            {isScheduled && (
              <span
                data-testid={`scheduled-badge-${order.id}`}
                className="bg-[#7c3aed] px-1.5 py-0.5 font-heading text-[9px] tracking-[0.2em] text-white"
                title={`Requested pickup: ${order.scheduled_for}`}
              >
                ⏰ {order.scheduled_for}
              </span>
            )}
            {isOverdue && (
              <span
                data-testid={`overdue-badge-${order.id}`}
                className="bg-red-600 px-1.5 py-0.5 font-heading text-[9px] tracking-[0.2em] text-white animate-pulse"
                title={`Ready for ${readyElapsedMin} min`}
              >
                OVERDUE {readyElapsedMin}m
              </span>
            )}
          </div>
          <div className="text-xs text-slate-900 font-medium">
            {order.customer_name}
          </div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
            {order.customer_phone}
          </div>
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-1.5">
            {onReprint && (
              <button
                data-testid={`reprint-${order.id}-btn`}
                onClick={(e) => {
                  e.stopPropagation();
                  // Pass the EXACT order captured in this card's closure —
                  // never index into an array. Fixes the recurring "reprint
                  // sent the wrong order" bug where the header shortcut
                  // always printed orders[0].
                  onReprint(order);
                  toast.success(`Reprinting ${order.order_number}`, { duration: 1500 });
                }}
                className="flex h-7 w-7 items-center justify-center border border-slate-300 text-slate-500 hover:border-[#1d4ed8] hover:text-[#1d4ed8]"
                title={`Reprint order ${order.order_number}`}
                aria-label={`Reprint order ${order.order_number}`}
              >
                <Printer className="h-3.5 w-3.5" />
              </button>
            )}
            <div className="font-heading text-lg text-slate-900 tabular">
              ${order.total.toFixed(2)}
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
            {elapsedMin}m ago · {new Date(order.created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}
          </div>
          <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400">
            {itemCount} {itemCount === 1 ? "item" : "items"}
          </div>
        </div>
      </div>

      {/* Line items — in Pending we show them full-size with per-line pricing
          so staff can see everything at a glance while assembling. */}
      <div
        className={`mt-2 space-y-1 border-t border-slate-200 pt-2 ${
          isPending ? "text-sm" : "text-xs"
        }`}
        data-testid="order-line-items"
      >
        {order.items.map((it, idx) => (
          <div
            key={`${it.item_id}-${idx}`}
            className={`flex items-start justify-between gap-2 ${
              isPending ? "border-b border-dashed border-slate-100 pb-1 last:border-b-0 last:pb-0" : ""
            }`}
          >
            <div className="flex-1 min-w-0">
              <div>
                <span className={`font-heading tabular text-[#1d4ed8] ${isPending ? "text-base" : "text-sm"}`}>
                  {it.quantity}×
                </span>{" "}
                <span className={`text-slate-900 ${isPending ? "font-medium" : ""}`}>
                  {it.name}
                </span>
              </div>
              {(it.variant_choice || it.side_choice || it.drink_choice) && (
                <div className="ml-6 mt-0.5 text-[10px] uppercase tracking-[0.15em] text-[#1d4ed8]">
                  {[it.variant_choice, it.side_choice, it.drink_choice].filter(Boolean).join(" · ")}
                </div>
              )}
              {Array.isArray(it.addons) && it.addons.length > 0 && (
                <div className="ml-6 mt-0.5 text-[10px] uppercase tracking-[0.15em] text-emerald-700">
                  {it.addons
                    .map((a) => `+ ${a.label}${a.qty > 1 ? ` ×${a.qty}` : ""}`)
                    .join(" · ")}
                </div>
              )}
            </div>
            {isPending && (
              <span className="text-xs tabular text-slate-500 whitespace-nowrap">
                ${(Number(it.price) * Number(it.quantity)).toFixed(2)}
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Full totals breakdown — only in Pending so the staff can verify the
          receipt vs. what Stripe collected before starting to cook. */}
      {isPending && (
        <div
          data-testid="pending-totals-breakdown"
          className="mt-2 space-y-0.5 border-t border-slate-200 bg-slate-50 px-2 py-2 text-xs"
        >
          <div className="flex items-center justify-between text-slate-500">
            <span>Subtotal</span>
            <span className="tabular">${Number(order.subtotal || 0).toFixed(2)}</span>
          </div>
          <div className="flex items-center justify-between text-slate-500">
            <span>Online fee</span>
            <span className="tabular">
              {wasFeeWaived ? (
                <span className="text-emerald-700 font-medium">WAIVED</span>
              ) : (
                `$${feeCharged.toFixed(2)}`
              )}
            </span>
          </div>
          {Number(order.tip || 0) > 0 && (
            <div className="flex items-center justify-between text-slate-500">
              <span>Tip</span>
              <span className="tabular text-[#0f766e]">+${Number(order.tip).toFixed(2)}</span>
            </div>
          )}
          <div className="flex items-center justify-between border-t border-slate-200 pt-1 font-heading text-sm text-slate-900">
            <span>TOTAL PAID</span>
            <span className="tabular">${Number(order.total).toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* Tip row for Preparing/Ready — compact */}
      {!isPending && order.tip > 0 && (
        <div className="mt-2 flex items-center justify-between text-xs">
          <span className="text-slate-500">Tip</span>
          <span className="font-heading text-sm text-[#1d4ed8] tabular">
            +${order.tip.toFixed(2)}
          </span>
        </div>
      )}
      {next && (
        <button
          data-testid={`advance-${order.id}-btn`}
          onClick={() => onAdvance(order.id, next.status)}
          className="mt-3 flex w-full items-center justify-center gap-2 bg-[#1d4ed8] py-2 font-heading text-sm tracking-wider text-white hover:bg-[#1e40af]"
        >
          {next.label}
          <Check className="h-4 w-4" strokeWidth={3} />
        </button>
      )}
    </div>
  );
}


function HistoryPanel({ client, onReprint }) {
  const [preset, setPreset] = useState("24h");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [minAmt, setMinAmt] = useState("");
  const [maxAmt, setMaxAmt] = useState("");
  const [hourStart, setHourStart] = useState("");
  const [hourEnd, setHourEnd] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const now = new Date();
      const params = {};
      if (preset === "24h") {
        params.from_ts = new Date(now.getTime() - 24 * 3600 * 1000).toISOString();
        params.to_ts = now.toISOString();
      } else if (preset === "48h") {
        params.from_ts = new Date(now.getTime() - 48 * 3600 * 1000).toISOString();
        params.to_ts = now.toISOString();
      } else if (preset === "30d") {
        params.from_ts = new Date(now.getTime() - 30 * 24 * 3600 * 1000).toISOString();
        params.to_ts = now.toISOString();
      } else if (preset === "custom") {
        if (customFrom) params.from_ts = new Date(customFrom).toISOString();
        if (customTo) params.to_ts = new Date(customTo).toISOString();
      }
      if (minAmt !== "") params.min_amount = Number(minAmt);
      if (maxAmt !== "") params.max_amount = Number(maxAmt);
      if (hourStart !== "") params.hour_start = Number(hourStart);
      if (hourEnd !== "") params.hour_end = Number(hourEnd);
      const { data } = await client.get("/admin/orders/history", { params });
      setRows(data);
    } catch (e) {
      toast.error("Failed to load history");
    } finally {
      setLoading(false);
    }
  }, [client, preset, customFrom, customTo, minAmt, maxAmt, hourStart, hourEnd]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const revenue = rows.reduce((a, r) => a + Number(r.total || 0), 0);
  const tipTotal = rows.reduce((a, r) => a + Number(r.tip || 0), 0);
  const waivedCount = rows.filter((r) => r.fee_waived).length;

  return (
    <div className="space-y-4">
      {/* Filter bar */}
      <div className="border-2 border-slate-900 bg-white p-4 space-y-3" data-testid="history-filters">
        <div className="flex flex-wrap gap-2">
          {[
            { k: "24h", label: "LAST 24H" },
            { k: "48h", label: "LAST 48H" },
            { k: "30d", label: "LAST 30D" },
            { k: "custom", label: "CUSTOM" },
          ].map((p) => (
            <button
              key={p.k}
              data-testid={`history-preset-${p.k}`}
              onClick={() => setPreset(p.k)}
              className={`border-2 px-4 py-2 font-heading text-xs tracking-wider ${
                preset === p.k
                  ? "border-[#1d4ed8] bg-[#1d4ed8] text-white"
                  : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="history-custom-range">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">From</span>
              <input
                data-testid="history-from-input"
                type="datetime-local"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                className="w-full border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">To</span>
              <input
                data-testid="history-to-input"
                type="datetime-local"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                className="w-full border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 border-t border-slate-200 pt-3">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Min $</span>
            <input
              data-testid="history-min-amt"
              type="number" min="0" step="0.5"
              value={minAmt}
              onChange={(e) => setMinAmt(e.target.value)}
              placeholder="0"
              className="w-full border border-slate-300 px-3 py-2 text-sm tabular"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Max $</span>
            <input
              data-testid="history-max-amt"
              type="number" min="0" step="0.5"
              value={maxAmt}
              onChange={(e) => setMaxAmt(e.target.value)}
              placeholder="—"
              className="w-full border border-slate-300 px-3 py-2 text-sm tabular"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Hour Start (0–23)</span>
            <input
              data-testid="history-hour-start"
              type="number" min="0" max="23" step="1"
              value={hourStart}
              onChange={(e) => setHourStart(e.target.value)}
              placeholder="—"
              className="w-full border border-slate-300 px-3 py-2 text-sm tabular"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Hour End (0–23)</span>
            <input
              data-testid="history-hour-end"
              type="number" min="0" max="23" step="1"
              value={hourEnd}
              onChange={(e) => setHourEnd(e.target.value)}
              placeholder="—"
              className="w-full border border-slate-300 px-3 py-2 text-sm tabular"
            />
          </label>
        </div>
        <div className="flex items-center justify-between pt-1">
          <div className="text-xs text-slate-500" data-testid="history-summary">
            {loading
              ? "Loading…"
              : `${rows.length} orders · Revenue $${revenue.toFixed(2)} · Tips $${tipTotal.toFixed(2)} · ${waivedCount} waived`}
          </div>
          <button
            data-testid="history-refresh-btn"
            onClick={fetchRows}
            className="border-2 border-slate-900 bg-white px-4 py-2 font-heading text-xs tracking-wider text-slate-900 hover:bg-slate-100"
          >
            REFRESH
          </button>
        </div>
      </div>

      {/* Results table */}
      <div className="border-2 border-slate-900 bg-white">
        <div className="grid grid-cols-12 gap-2 border-b border-slate-200 bg-slate-100 px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500">
          <div className="col-span-2">Order</div>
          <div className="col-span-3">When</div>
          <div className="col-span-2">Customer</div>
          <div className="col-span-2 text-right">Subtotal</div>
          <div className="col-span-1 text-right">Tip</div>
          <div className="col-span-1 text-right">Total</div>
          <div className="col-span-1 text-right">Print</div>
        </div>
        {rows.length === 0 ? (
          <div className="px-4 py-10 text-center text-xs uppercase tracking-[0.2em] text-slate-400" data-testid="history-empty">
            No orders in range
          </div>
        ) : (
          rows.map((o) => (
            <div
              key={o.id}
              data-testid={`history-row-${o.id}`}
              className="grid grid-cols-12 gap-2 items-center border-b border-slate-200 bg-white px-4 py-3 text-sm last:border-b-0"
            >
              <div className="col-span-2 font-heading text-base text-[#1d4ed8] tabular">{o.order_number}</div>
              <div className="col-span-3 text-xs text-slate-600">{new Date(o.created_at).toLocaleString()}</div>
              <div className="col-span-2 text-slate-900 truncate flex items-center gap-1.5">
                {o.customer_name}
                {o.fee_waived && (
                  <span
                    data-testid={`history-waived-badge-${o.id}`}
                    className="bg-emerald-600 px-1.5 py-0.5 font-heading text-[9px] tracking-[0.2em] text-white"
                    title="Comp code was used — online fee waived"
                  >
                    COMP
                  </span>
                )}
              </div>
              <div className="col-span-2 text-right tabular text-slate-700">${Number(o.subtotal || 0).toFixed(2)}</div>
              <div className="col-span-1 text-right tabular text-slate-700">${Number(o.tip || 0).toFixed(2)}</div>
              <div className="col-span-1 text-right font-heading text-base text-slate-900 tabular">${Number(o.total || 0).toFixed(2)}</div>
              <div className="col-span-1 flex items-center justify-end">
                <button
                  data-testid={`history-reprint-${o.id}-btn`}
                  onClick={() => onReprint && onReprint(o)}
                  className="border border-slate-300 bg-white p-1.5 text-slate-500 hover:border-[#1d4ed8] hover:text-[#1d4ed8]"
                  aria-label={`Reprint order ${o.order_number}`}
                  title="Reprint receipt"
                >
                  <Printer className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function MenuEditModal({ item, onClose, onSave }) {
  const isNew = !item;
  const [name, setName] = useState(item?.name || "");
  const [category, setCategory] = useState(item?.category || "Food");
  const [description, setDescription] = useState(item?.description || "");
  const [price, setPrice] = useState(item?.price != null ? String(item.price) : "");
  const [cost, setCost] = useState(item?.cost != null ? String(item.cost) : "0");
  const [variantsText, setVariantsText] = useState(
    Array.isArray(item?.variants) ? item.variants.join(", ") : ""
  );
  const [available, setAvailable] = useState(item ? item.available : true);
  // Add-ons (Cheese +$0.50, Extra Bacon +$1.00). Kept as an editable list of
  // {label, price, max_qty}. Empty list → item has no add-ons.
  const [addons, setAddons] = useState(
    Array.isArray(item?.addons)
      ? item.addons.map((a) => ({
          label: a.label || "",
          price: String(a.price ?? ""),
          max_qty: String(a.max_qty ?? 1),
        }))
      : []
  );
  // New items default to `discountable: true` so campaign promos apply unless
  // the admin explicitly opts them out (e.g. loss-leader combos, sponsor
  // freebies, or externally-comped items).
  const [discountable, setDiscountable] = useState(
    item?.discountable == null ? true : !!item.discountable
  );
  const [busy, setBusy] = useState(false);

  const CATS = ["Food", "Drinks", "Snacks", "Combos"];

  const addAddonRow = () => setAddons((prev) => [...prev, { label: "", price: "", max_qty: "1" }]);
  const updateAddon = (idx, patch) =>
    setAddons((prev) => prev.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  const removeAddon = (idx) => setAddons((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    const p = Number(price);
    const c = Number(cost || 0);
    if (!Number.isFinite(p) || p < 0) { toast.error("Price must be ≥ 0"); return; }
    if (!Number.isFinite(c) || c < 0) { toast.error("Cost must be ≥ 0"); return; }
    const variants = variantsText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    // Validate & normalise the add-on rows. Rows with an empty label are
    // silently dropped (admin scaffolded a row then didn't fill it).
    const cleanedAddons = [];
    for (const a of addons) {
      const label = (a.label || "").trim();
      if (!label) continue;
      const ap = Number(a.price);
      const mq = parseInt(a.max_qty, 10);
      if (!Number.isFinite(ap) || ap < 0) { toast.error(`Add-on "${label}" price must be ≥ 0`); return; }
      if (!Number.isInteger(mq) || mq < 1 || mq > 10) {
        toast.error(`Add-on "${label}" max qty must be 1–10`);
        return;
      }
      cleanedAddons.push({ label, price: ap, max_qty: mq });
    }
    const payload = {
      name: name.trim(),
      category,
      description: description.trim(),
      price: p,
      cost: c,
      available,
      discountable,
      variants: variants.length > 0 ? variants : null,
      addons: cleanedAddons.length > 0 ? cleanedAddons : null,
    };
    setBusy(true);
    try {
      await onSave(payload);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="menu-edit-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg border-2 border-slate-900 bg-white p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
              {isNew ? "Add" : "Edit"} Menu Item
            </div>
            <div className="font-heading text-2xl tracking-wider">
              {isNew ? "NEW ITEM" : name || "ITEM"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-900 font-heading text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="mt-5 space-y-3">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Name</span>
            <input
              data-testid="edit-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Cheeseburger"
              className="w-full border border-slate-300 px-3 py-2.5 text-slate-900 focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8]"
            />
          </label>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Category</span>
            <select
              data-testid="edit-category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full border border-slate-300 px-3 py-2.5 bg-white text-slate-900 focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8]"
            >
              {CATS.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
              Description <span className="text-slate-400 normal-case">(shown to customers)</span>
            </span>
            <textarea
              data-testid="edit-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Custom phrase / short description"
              className="w-full border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8] resize-none"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">Price ($)</span>
              <input
                data-testid="edit-price-input"
                type="number" min="0" step="0.25"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="4.50"
                className="w-full border border-slate-300 px-3 py-2.5 text-slate-900 tabular focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8]"
              />
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
                Cost ($) <span className="text-slate-400 normal-case">staff-only</span>
              </span>
              <input
                data-testid="edit-cost-input"
                type="number" min="0" step="0.25"
                value={cost}
                onChange={(e) => setCost(e.target.value)}
                placeholder="1.75"
                className="w-full border border-slate-300 px-3 py-2.5 text-slate-900 tabular focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8]"
              />
            </label>
          </div>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
              Variants <span className="text-slate-400 normal-case">comma-separated · leave empty for none</span>
            </span>
            <input
              data-testid="edit-variants-input"
              value={variantsText}
              onChange={(e) => setVariantsText(e.target.value)}
              placeholder="Grilled, Breaded"
              className="w-full border border-slate-300 px-3 py-2.5 text-slate-900 focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8]"
            />
            <span className="mt-1 block text-[10px] text-slate-500">
              If set, customers MUST pick one before adding to cart.
            </span>
          </label>

          <div className="border-t border-slate-200 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div>
                <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  Paid Add-ons
                </span>
                <span className="block text-[10px] text-slate-500 normal-case">
                  Optional extras the customer can tack on (e.g. Cheese, Bacon).
                </span>
              </div>
              <button
                type="button"
                data-testid="edit-addon-add-row"
                onClick={addAddonRow}
                className="border-2 border-[#1d4ed8] bg-white px-3 py-1 font-heading text-[10px] tracking-wider text-[#1d4ed8] hover:bg-blue-50"
              >
                + ADD
              </button>
            </div>
            {addons.length === 0 && (
              <div className="text-[10px] text-slate-400 italic">
                No add-ons. Tap ADD to create one.
              </div>
            )}
            <div className="space-y-2">
              {addons.map((a, i) => (
                <div
                  key={i}
                  data-testid={`edit-addon-row-${i}`}
                  className="grid grid-cols-[1fr_88px_72px_auto] gap-1.5 items-center"
                >
                  <input
                    data-testid={`edit-addon-label-${i}`}
                    value={a.label}
                    onChange={(e) => updateAddon(i, { label: e.target.value })}
                    placeholder="Cheese"
                    maxLength={40}
                    className="border border-slate-300 px-2 py-1.5 text-sm focus:border-[#1d4ed8] focus:outline-none"
                  />
                  <input
                    data-testid={`edit-addon-price-${i}`}
                    type="number"
                    min="0"
                    step="0.25"
                    value={a.price}
                    onChange={(e) => updateAddon(i, { price: e.target.value })}
                    placeholder="0.50"
                    className="border border-slate-300 px-2 py-1.5 text-sm tabular focus:border-[#1d4ed8] focus:outline-none"
                    title="Price added per unit"
                  />
                  <input
                    data-testid={`edit-addon-maxqty-${i}`}
                    type="number"
                    min="1"
                    max="10"
                    step="1"
                    value={a.max_qty}
                    onChange={(e) => updateAddon(i, { max_qty: e.target.value })}
                    className="border border-slate-300 px-2 py-1.5 text-sm tabular focus:border-[#1d4ed8] focus:outline-none"
                    title="Max quantity the customer can stack (1 = simple toggle)"
                  />
                  <button
                    type="button"
                    data-testid={`edit-addon-remove-${i}`}
                    onClick={() => removeAddon(i)}
                    className="border-2 border-slate-300 bg-white px-2 py-1.5 font-heading text-[10px] tracking-wider text-slate-600 hover:border-red-500 hover:text-red-600"
                    aria-label="Remove add-on"
                  >
                    ×
                  </button>
                </div>
              ))}
              {addons.length > 0 && (
                <div className="grid grid-cols-[1fr_88px_72px_auto] gap-1.5 text-[9px] uppercase tracking-[0.15em] text-slate-400 px-1">
                  <span>Label</span>
                  <span>Price ($)</span>
                  <span>Max Qty</span>
                  <span></span>
                </div>
              )}
            </div>
          </div>

          <label className="flex items-center gap-3 pt-1">
            <Switch
              data-testid="edit-available-toggle"
              checked={available}
              onCheckedChange={setAvailable}
            />
            <span className="text-sm text-slate-700">
              {available ? "In Stock" : "Sold Out"}
            </span>
          </label>

          <label className="flex items-start gap-3 pt-1 border-t border-slate-200 mt-2 pt-3">
            <Switch
              data-testid="edit-discountable-toggle"
              checked={discountable}
              onCheckedChange={setDiscountable}
            />
            <div className="flex-1">
              <span className="text-sm text-slate-700">
                {discountable ? "Eligible for promo discounts" : "Excluded from all promos"}
              </span>
              <div className="text-[10px] text-slate-500 mt-0.5">
                When off, no campaign code will ever discount this item — even
                if a campaign is scoped to include it.
              </div>
            </div>
          </label>
        </div>

        <div className="mt-6 flex items-center gap-2">
          <button
            onClick={onClose}
            className="flex-1 border-2 border-slate-300 bg-white py-2.5 font-heading text-sm tracking-wider text-slate-700 hover:border-slate-500"
          >
            CANCEL
          </button>
          <button
            data-testid="edit-save-btn"
            onClick={submit}
            disabled={busy}
            className="flex-1 border-2 border-[#1d4ed8] bg-[#1d4ed8] py-2.5 font-heading text-sm tracking-wider text-white hover:bg-[#1e40af] disabled:opacity-50"
          >
            {busy ? "SAVING…" : isNew ? "CREATE" : "SAVE"}
          </button>
        </div>
      </div>
    </div>
  );
}


function BulkImportModal({ client, onClose, onDone }) {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);

  // Parse CSV/TSV text into MenuItemCreate rows.
  // Expected header (case-insensitive): name, category, description, price, cost, variants
  // Any missing optional field defaults to empty / 0.
  const parse = () => {
    const text = raw.trim();
    if (!text) return { ok: [], errors: ["Paste some rows first"] };
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (lines.length === 0) return { ok: [], errors: ["No rows detected"] };
    // Auto-detect separator (tab wins if present, else comma)
    const sep = lines[0].includes("\t") ? "\t" : ",";
    const splitRow = (l) => l.split(sep).map((c) => c.trim());
    const header = splitRow(lines[0]).map((h) => h.toLowerCase());
    const idx = (k) => header.indexOf(k);
    const iName = idx("name");
    const iCat = idx("category");
    const iDesc = idx("description");
    const iPrice = idx("price");
    const iCost = idx("cost");
    const iVar = idx("variants");
    const errors = [];
    if (iName < 0 || iCat < 0 || iPrice < 0) {
      errors.push("Header row must include at least: name, category, price");
      return { ok: [], errors };
    }
    const CATS = ["Food", "Drinks", "Snacks", "Combos"];
    const ok = [];
    lines.slice(1).forEach((line, i) => {
      const cells = splitRow(line);
      const name = (cells[iName] || "").trim();
      const category = (cells[iCat] || "").trim();
      const priceRaw = cells[iPrice];
      if (!name) { errors.push(`Row ${i + 2}: name required`); return; }
      if (!CATS.includes(category)) {
        errors.push(`Row ${i + 2}: category must be one of ${CATS.join(", ")}`);
        return;
      }
      const price = Number(priceRaw);
      if (!Number.isFinite(price) || price < 0) {
        errors.push(`Row ${i + 2}: price invalid ("${priceRaw}")`);
        return;
      }
      const cost = iCost >= 0 ? Number(cells[iCost] || 0) : 0;
      const description = iDesc >= 0 ? (cells[iDesc] || "") : "";
      const variantsRaw = iVar >= 0 ? (cells[iVar] || "") : "";
      const variants = variantsRaw
        .split(/[|;/]/)
        .map((s) => s.trim())
        .filter(Boolean);
      ok.push({
        name,
        category,
        description,
        price,
        cost: Number.isFinite(cost) ? cost : 0,
        variants: variants.length > 0 ? variants : null,
      });
    });
    return { ok, errors };
  };

  const preview = raw.trim() ? parse() : { ok: [], errors: [] };

  const submit = async () => {
    const { ok, errors } = parse();
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }
    if (ok.length === 0) { toast.error("No valid rows to import"); return; }
    setBusy(true);
    try {
      const { data } = await client.post("/admin/menu/bulk", { items: ok });
      toast.success(`Imported ${data.inserted} items`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };

  const SAMPLE = `name,category,description,price,cost,variants
Hot Wings (6),Food,Six crispy wings with ranch,6.00,2.10,
Chicken Sandwich Deluxe,Food,House sauce & pickles,5.50,1.90,Grilled|Breaded
Gatorade,Drinks,20oz assorted flavors,3.00,1.25,
Popcorn,Snacks,Fresh popped,2.00,0.40,`;

  return (
    <div
      data-testid="bulk-import-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl border-2 border-slate-900 bg-white p-6 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
              Menu — Bulk Import
            </div>
            <div className="font-heading text-2xl tracking-wider">PASTE ROWS</div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-900 font-heading text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="mt-4 space-y-2 border-l-4 border-[#1d4ed8] bg-slate-50 p-3 text-xs text-slate-600">
          <p className="font-medium text-slate-900">Format: comma or tab separated · first row is the header.</p>
          <p>Required columns: <span className="tabular font-medium">name, category, price</span></p>
          <p>Optional: <span className="tabular">description, cost, variants</span> (pipe-separated, e.g. <span className="tabular">Grilled|Breaded</span>)</p>
          <p>Categories: <span className="tabular">Food · Drinks · Snacks · Combos</span></p>
          <button
            data-testid="bulk-load-sample-btn"
            onClick={() => setRaw(SAMPLE)}
            className="text-[#1d4ed8] underline underline-offset-2 hover:text-[#1e40af]"
          >
            Load example rows
          </button>
        </div>

        <textarea
          data-testid="bulk-import-textarea"
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          rows={10}
          placeholder="name,category,price,cost&#10;Hot Wings (6),Food,6.00,2.10"
          className="mt-4 w-full border border-slate-300 px-3 py-2 text-sm tabular text-slate-900 focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8]"
        />

        {raw.trim() && (
          <div className="mt-3 flex items-center justify-between text-xs">
            <span className="text-slate-600" data-testid="bulk-preview-count">
              {preview.ok.length} valid row{preview.ok.length === 1 ? "" : "s"}
              {preview.errors.length > 0 && (
                <span className="ml-2 text-red-600">
                  · {preview.errors.length} error{preview.errors.length === 1 ? "" : "s"}
                </span>
              )}
            </span>
          </div>
        )}
        {preview.errors.length > 0 && (
          <ul className="mt-2 max-h-24 overflow-auto border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            {preview.errors.slice(0, 6).map((e, i) => (
              <li key={`${i}-${e}`}>· {e}</li>
            ))}
            {preview.errors.length > 6 && <li>… and {preview.errors.length - 6} more</li>}
          </ul>
        )}

        <div className="mt-5 flex items-center gap-2">
          <button
            onClick={onClose}
            className="flex-1 border-2 border-slate-300 bg-white py-2.5 font-heading text-sm tracking-wider text-slate-700 hover:border-slate-500"
          >
            CANCEL
          </button>
          <button
            data-testid="bulk-import-submit-btn"
            onClick={submit}
            disabled={busy || preview.ok.length === 0 || preview.errors.length > 0}
            className="flex-1 border-2 border-[#1d4ed8] bg-[#1d4ed8] py-2.5 font-heading text-sm tracking-wider text-white hover:bg-[#1e40af] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "IMPORTING…" : `IMPORT ${preview.ok.length}`}
          </button>
        </div>
      </div>
    </div>
  );
}


function OwnerSettingsModal({ client, settings, onClose, onSaved }) {
  const [step, setStep] = useState("password");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [surcharge, setSurcharge] = useState(
    settings?.surcharge != null ? String(settings.surcharge) : "1.00"
  );
  const [orderLimit, setOrderLimit] = useState(
    settings?.order_subtotal_limit != null ? String(settings.order_subtotal_limit) : "20.00"
  );

  const verify = async () => {
    if (!password.trim()) return;
    setBusy(true);
    try {
      const { data } = await client.post("/admin/owner/verify", { password });
      if (data.ok) {
        setStep("settings");
      } else {
        toast.error("Incorrect owner password");
        setPassword("");
      }
    } catch {
      toast.error("Verification failed");
    } finally {
      setBusy(false);
    }
  };

  const savePricing = async () => {
    const s = Number(surcharge);
    const lim = Number(orderLimit);
    if (!Number.isFinite(s) || s < 0 || s > 10) {
      toast.error("Surcharge must be between $0 and $10");
      return;
    }
    if (!Number.isFinite(lim) || lim < 1 || lim > 500) {
      toast.error("Order limit must be between $1 and $500");
      return;
    }
    setBusy(true);
    try {
      // Send both fields — server only updates changed ones vs. current.
      const patch = {};
      if (s !== Number(settings?.surcharge)) patch.surcharge = s;
      if (lim !== Number(settings?.order_subtotal_limit)) patch.order_subtotal_limit = lim;
      if (Object.keys(patch).length === 0) {
        toast.info("Nothing to change");
        setBusy(false);
        return;
      }
      const { data } = await client.patch(
        "/admin/settings",
        patch,
        { headers: { "x-owner-password": password } }
      );
      toast.success("Owner settings saved");
      onSaved(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="owner-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md border-2 border-slate-900 bg-white p-6"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
              Owner Only
            </div>
            <div className="font-heading text-2xl tracking-wider">
              {step === "password" ? "ENTER OWNER PASSWORD" : "OWNER SETTINGS"}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-900 font-heading text-2xl leading-none"
          >
            ×
          </button>
        </div>

        {step === "password" && (
          <div className="mt-5 space-y-3">
            <p className="text-xs text-slate-600">
              This section changes app-wide pricing. Enter the owner password
              to continue.
            </p>
            <input
              data-testid="owner-password-input"
              type="password"
              inputMode="numeric"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") verify(); }}
              placeholder="Password"
              className="w-full border border-slate-300 px-3 py-2.5 text-slate-900 tabular focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8]"
            />
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={onClose}
                className="flex-1 border-2 border-slate-300 bg-white py-2.5 font-heading text-sm tracking-wider text-slate-700 hover:border-slate-500"
              >
                CANCEL
              </button>
              <button
                data-testid="owner-verify-btn"
                onClick={verify}
                disabled={busy || !password.trim()}
                className="flex-1 border-2 border-[#1d4ed8] bg-[#1d4ed8] py-2.5 font-heading text-sm tracking-wider text-white hover:bg-[#1e40af] disabled:opacity-40"
              >
                {busy ? "…" : "UNLOCK"}
              </button>
            </div>
          </div>
        )}

        {step === "settings" && (
          <div className="mt-5 space-y-4">
            {settings?.orders_open ? (
              <div
                data-testid="owner-orders-open-warning"
                className="border-2 border-yellow-500 bg-yellow-50 p-3 text-xs text-slate-700"
              >
                <div className="font-heading text-sm tracking-wider text-yellow-800">
                  ORDERS ARE OPEN
                </div>
                <div className="mt-1">
                  Pause customer orders (top-right toggle) before changing
                  pricing — this prevents a mid-cart price flip.
                </div>
              </div>
            ) : (
              <div className="border-l-4 border-[#1d4ed8] bg-blue-50 p-3 text-xs text-slate-600">
                Orders are paused — safe to change pricing. Any new customer
                checkout will use the new amounts immediately.
              </div>
            )}
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
                Online Order Surcharge ($)
              </span>
              <input
                data-testid="owner-surcharge-input"
                type="number" min="0" max="10" step="0.25"
                value={surcharge}
                disabled={settings?.orders_open}
                onChange={(e) => setSurcharge(e.target.value)}
                className="w-full border border-slate-300 px-3 py-2.5 text-slate-900 tabular focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8] disabled:bg-slate-100 disabled:text-slate-500"
              />
              <span className="mt-1 block text-[10px] text-slate-500">
                Current: ${Number(settings?.surcharge ?? 0).toFixed(2)}
              </span>
            </label>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
                Max Order Subtotal ($)
              </span>
              <input
                data-testid="owner-order-limit-input"
                type="number" min="1" max="500" step="1"
                value={orderLimit}
                disabled={settings?.orders_open}
                onChange={(e) => setOrderLimit(e.target.value)}
                className="w-full border border-slate-300 px-3 py-2.5 text-slate-900 tabular focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8] disabled:bg-slate-100 disabled:text-slate-500"
              />
              <span className="mt-1 block text-[10px] text-slate-500">
                Current: ${Number(settings?.order_subtotal_limit ?? 20).toFixed(2)} · applies
                to the items subtotal (fee & tip not included).
              </span>
            </label>
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={onClose}
                className="flex-1 border-2 border-slate-300 bg-white py-2.5 font-heading text-sm tracking-wider text-slate-700 hover:border-slate-500"
              >
                CANCEL
              </button>
              <button
                data-testid="owner-save-btn"
                onClick={savePricing}
                disabled={busy || settings?.orders_open}
                className="flex-1 border-2 border-[#1d4ed8] bg-[#1d4ed8] py-2.5 font-heading text-sm tracking-wider text-white hover:bg-[#1e40af] disabled:opacity-40"
              >
                {busy ? "SAVING…" : "SAVE SETTINGS"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function PromosPanel({ client }) {
  const [summary, setSummary] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingDeputy, setSavingDeputy] = useState(false);
  const [deputyCode, setDeputyCode] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [drilledCampaign, setDrilledCampaign] = useState(null); // {campaign, codes, filter}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, m] = await Promise.all([
        client.get("/admin/promos/summary"),
        client.get("/menu"),
      ]);
      setSummary(s.data);
      setMenuItems(m.data || []);
      setDeputyCode(s.data?.deputy?.code || "");
    } catch {
      toast.error("Failed to load promos");
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (patch) => {
    try {
      await client.patch("/admin/settings", patch);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const saveDeputyCode = async () => {
    const code = (deputyCode || "").trim().toUpperCase();
    if (code.length < 4 || code.length > 32) {
      toast.error("Deputy code must be 4–32 characters");
      return;
    }
    setSavingDeputy(true);
    try {
      await client.patch("/admin/settings", { deputy_code: code });
      toast.success(`Deputy code set: ${code}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingDeputy(false);
    }
  };

  const deleteCampaign = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"? Unused codes will be wiped. Used codes stay in the order history.`)) return;
    try {
      await client.delete(`/admin/promo-campaigns/${c.id}`);
      toast.success("Campaign deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const toggleActive = async (c) => {
    try {
      await client.patch(`/admin/promo-campaigns/${c.id}`, { active: !c.active });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const openDrilldown = async (c, filter = "all") => {
    try {
      const { data } = await client.get(`/admin/promo-campaigns/${c.id}/codes`, {
        params: { filter_by: filter },
      });
      setDrilledCampaign({ campaign: data.campaign, codes: data.codes, filter });
    } catch {
      toast.error("Could not load codes");
    }
  };

  const printHandout = async (c) => {
    try {
      const r = await client.get(`/admin/promo-campaigns/${c.id}/handout`, {
        responseType: "text",
      });
      const blob = new Blob([r.data], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        toast.error("Enable pop-ups to open the handout");
        URL.revokeObjectURL(url);
        return;
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      toast.error("Could not open handout");
    }
  };

  return (
    <div className="space-y-4" data-testid="promos-panel">
      {/* Sheriff Deputy card */}
      <div className="border-2 border-slate-900 bg-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
              Sheriff Deputy Promo
            </div>
            <div className="font-heading text-2xl tracking-wider">
              {summary?.deputy?.enabled ? "ACTIVE" : "OFF"}
            </div>
            <div className="text-xs text-slate-600 mt-1">
              Reusable code · waives food total + fee to $0. Tip still charged.
            </div>
          </div>
          <label className="flex items-center gap-2 border-2 border-slate-900 bg-white px-3 py-1.5 cursor-pointer">
            <span className="font-heading text-xs tracking-[0.2em] text-slate-900">
              {summary?.deputy?.enabled ? "ON" : "OFF"}
            </span>
            <Switch
              data-testid="deputy-promo-toggle"
              checked={!!summary?.deputy?.enabled}
              onCheckedChange={(v) => toggle({ deputy_promo_enabled: v })}
            />
          </label>
        </div>
        <div className="mt-3 flex items-end gap-2">
          <label className="flex-1">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
              Deputy Code
            </span>
            <input
              data-testid="deputy-code-input"
              type="text"
              value={deputyCode}
              onChange={(e) => setDeputyCode(e.target.value.toUpperCase())}
              className="w-full border border-slate-300 px-3 py-2 text-slate-900 tabular focus:border-[#1d4ed8] focus:outline-none focus:ring-1 focus:ring-[#1d4ed8]"
              placeholder="DEPUTYFREE"
            />
          </label>
          <button
            data-testid="deputy-code-save-btn"
            onClick={saveDeputyCode}
            disabled={savingDeputy || (deputyCode || "").trim() === (summary?.deputy?.code || "").trim()}
            className="border-2 border-[#1d4ed8] bg-[#1d4ed8] px-4 py-2 font-heading text-xs tracking-wider text-white hover:bg-[#1e40af] disabled:opacity-40"
          >
            {savingDeputy ? "SAVING…" : "SAVE CODE"}
          </button>
        </div>
      </div>

      {/* Campaigns header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
            Promo Campaigns
          </div>
          <div className="font-heading text-xl tracking-wider text-slate-900">
            ONE-TIME CODES
          </div>
        </div>
        <button
          data-testid="new-campaign-btn"
          onClick={() => setShowCreate(true)}
          className="border-2 border-[#1d4ed8] bg-[#1d4ed8] px-4 py-2 font-heading text-xs tracking-wider text-white hover:bg-[#1e40af]"
        >
          + NEW CAMPAIGN
        </button>
      </div>

      {/* Campaigns list */}
      <div className="space-y-3">
        {loading && (
          <div className="border-2 border-slate-200 bg-white p-6 text-center text-xs uppercase tracking-[0.2em] text-slate-400">
            Loading…
          </div>
        )}
        {!loading && (summary?.campaigns || []).length === 0 && (
          <div
            data-testid="campaigns-empty"
            className="border-2 border-dashed border-slate-300 bg-white p-6 text-center"
          >
            <div className="font-heading text-sm tracking-wider text-slate-700">
              NO CAMPAIGNS YET
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Create a campaign to mint one-time-use promo codes (e.g. 1000
              codes at $5 off, band-student handouts, sponsor giveaways).
            </div>
          </div>
        )}
        {(summary?.campaigns || []).map((c) => {
          const dtype = c.discount_type;
          const amt = Number(c.discount_amount || 0);
          const label = dtype === "percent" ? `${amt}% off` : `$${amt.toFixed(2)} off`;
          const scope = (c.eligible_item_ids || []).length === 0
            ? "All discountable items"
            : `${(c.eligible_item_ids || []).length} item${(c.eligible_item_ids || []).length === 1 ? "" : "s"}`;
          return (
            <div
              key={c.id}
              data-testid={`campaign-card-${c.id}`}
              className="border-2 border-slate-900 bg-white p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-heading text-lg tracking-wider text-slate-900 truncate">
                      {c.name}
                    </div>
                    {!c.active && (
                      <span className="border border-slate-400 px-1.5 py-0.5 font-heading text-[9px] tracking-[0.2em] text-slate-500">
                        PAUSED
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    <span className="font-heading tracking-wider text-[#1d4ed8]">{label}</span>
                    <span className="text-slate-400 mx-1.5">·</span>
                    <span>{scope}</span>
                  </div>
                </div>
                <label className="flex items-center gap-1.5 border-2 border-slate-900 bg-white px-2 py-1 cursor-pointer">
                  <span className="font-heading text-[10px] tracking-[0.2em] text-slate-900">
                    {c.active ? "ON" : "OFF"}
                  </span>
                  <Switch
                    data-testid={`campaign-toggle-${c.id}`}
                    checked={!!c.active}
                    onCheckedChange={() => toggleActive(c)}
                  />
                </label>
              </div>

              <div className="mt-3 grid grid-cols-3 gap-2 border-t border-slate-200 pt-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Total</div>
                  <div className="font-heading text-xl tabular" data-testid={`campaign-total-${c.id}`}>
                    {c.total ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Used</div>
                  <div className="font-heading text-xl tabular text-red-600" data-testid={`campaign-used-${c.id}`}>
                    {c.used ?? 0}
                  </div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500">Unused</div>
                  <div className="font-heading text-xl tabular text-emerald-600" data-testid={`campaign-unused-${c.id}`}>
                    {c.unused ?? 0}
                  </div>
                </div>
              </div>

              <div className="mt-3 flex flex-wrap gap-2 border-t border-slate-200 pt-3">
                <button
                  data-testid={`campaign-view-codes-${c.id}`}
                  onClick={() => openDrilldown(c)}
                  className="border-2 border-slate-900 bg-white px-3 py-1.5 font-heading text-[11px] tracking-wider text-slate-900 hover:bg-slate-100"
                >
                  VIEW CODES
                </button>
                <button
                  data-testid={`campaign-print-${c.id}`}
                  onClick={() => printHandout(c)}
                  disabled={!c.unused}
                  className="border-2 border-[#1d4ed8] bg-white px-3 py-1.5 font-heading text-[11px] tracking-wider text-[#1d4ed8] hover:bg-blue-50 disabled:opacity-40"
                >
                  PRINT HANDOUT
                </button>
                <button
                  data-testid={`campaign-delete-${c.id}`}
                  onClick={() => deleteCampaign(c)}
                  className="ml-auto border-2 border-red-600 bg-white px-3 py-1.5 font-heading text-[11px] tracking-wider text-red-600 hover:bg-red-50"
                >
                  DELETE
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create modal */}
      {showCreate && (
        <CampaignCreateModal
          client={client}
          menuItems={menuItems}
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            load();
          }}
        />
      )}

      {/* Drilldown modal */}
      {drilledCampaign && (
        <CampaignCodesModal
          client={client}
          state={drilledCampaign}
          setState={setDrilledCampaign}
          onClose={() => setDrilledCampaign(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}


function CampaignCreateModal({ client, menuItems, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [discountType, setDiscountType] = useState("flat");
  const [discountAmount, setDiscountAmount] = useState("5");
  const [codeCount, setCodeCount] = useState("1000");
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [scopeMode, setScopeMode] = useState("all"); // all | picker
  const [busy, setBusy] = useState(false);

  const toggleItem = (id) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    const nm = name.trim();
    const amt = Number(discountAmount);
    const cnt = parseInt(codeCount, 10);
    if (nm.length < 2) return toast.error("Give the campaign a name");
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Discount must be > 0");
    if (discountType === "percent" && amt > 100) return toast.error("Percent must be ≤ 100");
    if (!Number.isInteger(cnt) || cnt < 1 || cnt > 5000) {
      return toast.error("Code count must be 1–5000");
    }
    const eligible = scopeMode === "all" ? [] : Array.from(selectedItems);
    if (scopeMode === "picker" && eligible.length === 0) {
      return toast.error("Pick at least one item, or switch to All items");
    }
    setBusy(true);
    try {
      await client.post("/admin/promo-campaigns", {
        name: nm,
        discount_type: discountType,
        discount_amount: amt,
        code_count: cnt,
        eligible_item_ids: eligible,
        active: true,
      });
      toast.success(`Created "${nm}" with ${cnt} codes`);
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Create failed");
    } finally {
      setBusy(false);
    }
  };

  // Only offer items flagged discountable=true as pickable — the rest can
  // never be discounted anyway.
  const eligibleMenu = menuItems.filter((m) => m.discountable !== false);
  const excludedCount = menuItems.length - eligibleMenu.length;

  return (
    <div
      data-testid="campaign-create-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto border-2 border-slate-900 bg-white p-5"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
              Create Campaign
            </div>
            <div className="font-heading text-xl tracking-wider text-slate-900">
              NEW PROMO CODES
            </div>
          </div>
          <button
            data-testid="campaign-create-close"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
              Campaign Name
            </span>
            <input
              data-testid="campaign-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="e.g. Band Students $5 Off · Homecoming 2026"
              className="w-full border border-slate-300 px-3 py-2 text-slate-900 focus:border-[#1d4ed8] focus:outline-none"
            />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
                Discount Type
              </span>
              <div className="flex gap-1">
                {[
                  { k: "flat", label: "$ OFF" },
                  { k: "percent", label: "% OFF" },
                ].map((t) => (
                  <button
                    key={t.k}
                    data-testid={`campaign-type-${t.k}`}
                    onClick={() => setDiscountType(t.k)}
                    className={`flex-1 border-2 py-2 font-heading text-[11px] tracking-wider ${
                      discountType === t.k
                        ? "border-[#1d4ed8] bg-[#1d4ed8] text-white"
                        : "border-slate-300 bg-white text-slate-700"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
                Amount ({discountType === "percent" ? "%" : "$"})
              </span>
              <input
                data-testid="campaign-amount-input"
                type="number"
                min="0.01"
                max={discountType === "percent" ? "100" : "500"}
                step={discountType === "percent" ? "1" : "0.25"}
                value={discountAmount}
                onChange={(e) => setDiscountAmount(e.target.value)}
                className="w-full border border-slate-300 px-3 py-2 text-slate-900 tabular focus:border-[#1d4ed8] focus:outline-none"
              />
            </label>
          </div>

          <label className="block">
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
              How many one-time codes? (1–5000)
            </span>
            <input
              data-testid="campaign-count-input"
              type="number"
              min="1"
              max="5000"
              step="1"
              value={codeCount}
              onChange={(e) => setCodeCount(e.target.value)}
              className="w-full border border-slate-300 px-3 py-2 text-slate-900 tabular focus:border-[#1d4ed8] focus:outline-none"
            />
          </label>

          <div>
            <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1">
              Applies To
            </span>
            <div className="flex gap-1 mb-2">
              {[
                { k: "all", label: "ALL DISCOUNTABLE ITEMS" },
                { k: "picker", label: "PICK ITEMS…" },
              ].map((s) => (
                <button
                  key={s.k}
                  data-testid={`campaign-scope-${s.k}`}
                  onClick={() => setScopeMode(s.k)}
                  className={`flex-1 border-2 py-2 font-heading text-[10px] tracking-wider ${
                    scopeMode === s.k
                      ? "border-[#1d4ed8] bg-[#1d4ed8] text-white"
                      : "border-slate-300 bg-white text-slate-700"
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            {scopeMode === "all" && excludedCount > 0 && (
              <div className="text-[10px] text-slate-500">
                {excludedCount} menu item{excludedCount === 1 ? "" : "s"} flagged
                non-discountable will still be excluded.
              </div>
            )}
            {scopeMode === "picker" && (
              <div className="max-h-52 overflow-y-auto border border-slate-300 divide-y divide-slate-100">
                {eligibleMenu.length === 0 && (
                  <div className="p-3 text-center text-xs text-slate-500">
                    All menu items are flagged non-discountable. Toggle at
                    least one item in the Menu tab to make it eligible.
                  </div>
                )}
                {eligibleMenu.map((m) => (
                  <label
                    key={m.id}
                    className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      data-testid={`campaign-item-${m.id}`}
                      checked={selectedItems.has(m.id)}
                      onChange={() => toggleItem(m.id)}
                      className="h-4 w-4 accent-[#1d4ed8]"
                    />
                    <span className="flex-1 text-sm">{m.name}</span>
                    <span className="text-xs text-slate-500 tabular">
                      ${Number(m.price).toFixed(2)}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="mt-5 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 border-2 border-slate-300 bg-white py-2.5 font-heading text-sm tracking-wider text-slate-700 hover:border-slate-500"
          >
            CANCEL
          </button>
          <button
            data-testid="campaign-create-submit"
            onClick={submit}
            disabled={busy}
            className="flex-1 border-2 border-[#1d4ed8] bg-[#1d4ed8] py-2.5 font-heading text-sm tracking-wider text-white hover:bg-[#1e40af] disabled:opacity-40"
          >
            {busy ? "GENERATING…" : "CREATE CAMPAIGN"}
          </button>
        </div>
      </div>
    </div>
  );
}


function CampaignCodesModal({ client, state, setState, onClose, onChanged }) {
  const { campaign, codes, filter } = state;

  const setFilter = async (f) => {
    try {
      const { data } = await client.get(`/admin/promo-campaigns/${campaign.id}/codes`, {
        params: { filter_by: f },
      });
      setState({ campaign: data.campaign, codes: data.codes, filter: f });
    } catch {
      toast.error("Filter failed");
    }
  };

  const resetCode = async (code) => {
    if (!window.confirm(`Mark ${code} as UNUSED again? It becomes redeemable.`)) return;
    try {
      await client.post(`/admin/promo-campaigns/${campaign.id}/codes/${code}/reset`, {});
      toast.success(`${code} reset`);
      setFilter(filter);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
  };

  const downloadCsv = () => {
    if (!codes.length) return;
    const rows = [
      ["code", "used", "used_at", "used_by_order_id"],
      ...codes.map((c) => [c.code, c.used ? "yes" : "no", c.used_at || "", c.used_by_order_id || ""]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(campaign.name || "campaign").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-${filter}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <div
      data-testid="campaign-codes-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-3"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] flex flex-col border-2 border-slate-900 bg-white"
      >
        <div className="border-b-2 border-slate-900 bg-slate-50 px-4 py-3 flex items-center justify-between">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#1d4ed8]">
              Campaign Codes
            </div>
            <div className="font-heading text-lg tracking-wider text-slate-900 truncate">
              {campaign.name}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-slate-900 text-2xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-2">
          {[
            { k: "all", label: `ALL (${campaign.total})` },
            { k: "unused", label: `UNUSED (${campaign.unused})` },
            { k: "used", label: `USED (${campaign.used})` },
          ].map((f) => (
            <button
              key={f.k}
              data-testid={`codes-filter-${f.k}`}
              onClick={() => setFilter(f.k)}
              className={`border-2 px-3 py-1 font-heading text-[10px] tracking-wider ${
                filter === f.k
                  ? "border-[#1d4ed8] bg-[#1d4ed8] text-white"
                  : "border-slate-300 bg-white text-slate-700"
              }`}
            >
              {f.label}
            </button>
          ))}
          <button
            data-testid="codes-csv-btn"
            onClick={downloadCsv}
            disabled={!codes.length}
            className="ml-auto border-2 border-slate-900 bg-white px-3 py-1 font-heading text-[10px] tracking-wider text-slate-900 hover:bg-slate-100 disabled:opacity-40"
          >
            DOWNLOAD CSV
          </button>
        </div>

        <div className="overflow-auto flex-1">
          <table className="w-full text-xs" data-testid="campaign-codes-table">
            <thead className="sticky top-0 bg-white border-b border-slate-200">
              <tr className="text-left text-slate-500">
                <th className="px-3 py-2 uppercase tracking-[0.15em]">Code</th>
                <th className="px-3 py-2 uppercase tracking-[0.15em]">Status</th>
                <th className="px-3 py-2 uppercase tracking-[0.15em]">Used At</th>
                <th className="px-3 py-2 uppercase tracking-[0.15em] text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {codes.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-slate-400 uppercase tracking-[0.2em]">
                    No codes
                  </td>
                </tr>
              )}
              {codes.map((c) => (
                <tr key={c.code} data-testid={`codes-row-${c.code}`}>
                  <td className="px-3 py-1.5 font-heading tabular text-slate-900">{c.code}</td>
                  <td className="px-3 py-1.5">
                    {c.used ? (
                      <span className="bg-red-600 px-1.5 py-0.5 font-heading text-[9px] tracking-[0.2em] text-white">
                        USED
                      </span>
                    ) : (
                      <span className="border border-emerald-600 px-1.5 py-0.5 font-heading text-[9px] tracking-[0.2em] text-emerald-700">
                        UNUSED
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 tabular text-slate-500 text-[10px]">
                    {c.used_at ? new Date(c.used_at).toLocaleString([], { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" }) : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {c.used && (
                      <button
                        data-testid={`codes-reset-${c.code}-btn`}
                        onClick={() => resetCode(c.code)}
                        className="border border-slate-300 px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] text-slate-500 hover:border-[#1d4ed8] hover:text-[#1d4ed8]"
                      >
                        Reset
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

